import { useEffect, useState } from 'react';
import { getToken } from '../lib/api';
import { fmtMoney, useT } from '../lib/i18n';
import { alert, confirm, haptic, openExternal } from '../lib/telegram';
import { GrowArea } from './GrowArea';
import { loadMyJournal } from './Journal';
import { PinnedSheet, SetRow } from './SetSheet';
import { fmtShortDay, fmtTime } from '../lib/time';

const BASE = import.meta.env.VITE_API_URL ?? '';

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { ...(init.body == null ? {} : { 'content-type': 'application/json' }),
      authorization: `Bearer ${getToken()}`, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `http_${res.status}`);
  }
  return res.json();
}

/**
 * The coach's «Инфо» card (server: coach/info.ts).
 *
 * Address, map link, how to find them and anything else, as the coach
 * wrote them; prices and the cancellation rule read from today's
 * settings. Clients see it on My profile only once the coach has filled
 * in the address or "how to find us".
 */

export interface Info {
  coachName: string; filled: boolean;
  address: string | null; mapsUrl: string | null; directions: string | null; notes: string | null;
  prices: { kind: 'private' | 'group'; minutes: number; price: number }[];
  cancelHours: number;
  /** «Клуб / город» (the player's collapsed row shows it under «Инфо · {coach}»). */
  club?: string | null;
}

/**
 * The card as a client sees it: one block per part, each with an icon
 * and a small heading, space between them, and empty blocks left out.
 * No raw link anywhere: the map is a button.
 */
export function InfoCard({ info, noTitle }: { info: Info; noTitle?: boolean }) {
  const t = useT();
  const block = (icon: string, head: Parameters<typeof t>[0], children: React.ReactNode) => (
    <div className="card iblock">
      <div className="iblock__head"><span aria-hidden="true">{icon}</span>{t(head)}</div>
      {children}
    </div>
  );
  return (
    <div className="info">
      {!noTitle && <div className="info__title">{t('info.title', { coach: info.coachName })}</div>}
      {(info.address || info.mapsUrl) && block('📍', 'info.where', <>
        {info.address && <p>{info.address}</p>}
        {info.mapsUrl && (
          <button className="iblock__map" onClick={() => { haptic.tap(); openExternal(info.mapsUrl!); }}>
            {t('info.openMaps')}
          </button>
        )}
      </>)}
      {info.directions && block('🧭', 'info.find', <p>{info.directions}</p>)}
      {info.prices.length > 0 && block('💶', 'info.prices',
        <div className="info__price"><span>{t('info.hour')}</span><b>{fmtMoney(Math.round(info.prices[0].price))}</b></div>)}
      {block('↩️', 'info.cancel', <>
          <p>{t('info.cancelFree', { h: info.cancelHours, hours: t.word('info.hoursW', info.cancelHours) })}</p>
          <p>{t('info.cancelLate')}</p>
        </>)}
      {info.notes && block('ℹ️', 'info.goodToKnow', <p>{info.notes}</p>)}
    </div>
  );
}

/** Whether the player last left «Инфо» open or closed; null until they choose. Per device. */
const INFO_OPEN = 'pd.info.open';
const remembered = (): boolean | null => {
  try { const v = localStorage.getItem(INFO_OPEN); return v === null ? null : v === '1'; } catch { return null; }
};
const remember = (open: boolean) => { try { localStorage.setItem(INFO_OPEN, open ? '1' : '0'); } catch { /* private mode */ } };

/**
 * My profile, at the bottom: the coach's «Инфо» as one row — «ℹ️ Инфо ·
 * {coach}», the club under it — that opens in place to the cards
 * (where, how to find, prices, cancelling) and stays open until closed.
 * Open from the start for someone with no lessons yet, who needs it most.
 * Nothing until the coach has filled it in.
 */
export function ClientInfo() {
  const t = useT();
  const [info, setInfo] = useState<Info | null>(null);
  const [open, setOpen] = useState<boolean>(() => remembered() ?? false);
  useEffect(() => {
    api<{ info: Info | null }>('/api/coach-info').then((r) => setInfo(r.info)).catch(() => setInfo(null));
    // No lessons yet: open, unless they closed it before (the journal lists the lessons had).
    if (remembered() === null) loadMyJournal().then((j) => setOpen(j.sessions.length === 0)).catch(() => {});
  }, []);
  if (!info) return null;
  const toggle = () => { haptic.tap(); setOpen(!open); remember(!open); };
  const sub = info.club || info.address?.split('\n')[0] || null;
  return (
    <div className={`info-wrap info-fold${open ? ' info-fold--open' : ''}`}>
      <button className="info-row" aria-expanded={open} onClick={toggle}>
        <span className="info-row__icon" aria-hidden="true">ℹ️</span>
        <span className="info-row__main">
          <b>{t('info.title', { coach: info.coachName })}</b>
          {sub && <small>{sub}</small>}
        </span>
        <span className="info-row__chev" aria-hidden="true">{open ? '⌃' : '›'}</span>
      </button>
      {open && (
        <>
          <InfoCard info={info} noTitle />
          <button className="sess__link info-row__close" onClick={toggle}>{t('info.close')}</button>
        </>
      )}
    </div>
  );
}

export interface CoachView extends Info {
  reachable: number; notInBot: number;
  history: { id: string; kind: string; recipients: number; created_at: string }[];
}

export const loadCoachInfo = () => api<CoachView>('/api/coach/info');

/** Settings: the one row. «Заполнено · отправлено 4 окт.» or «Не заполнено». */
export function InfoRow({ view, onOpen, dot }: { view: CoachView | null; onOpen: () => void; dot?: boolean }) {
  const t = useT();
  const last = view?.history[0];
  const status = !view ? '…' : view.filled
    ? <><span className="setrow__ok">{t('co.info.statusFilled')}</span>
        {last ? ` · ${t('co.info.statusSent', { when: fmtShortDay(new Date(last.created_at)) })}` : ` · ${t('co.info.statusNotSent')}`}</>
    : <span className="setrow__no">{t('co.info.statusEmpty')}</span>;
  return <SetRow id="setup-info" icon="👤" title={t('co.set.rowProfile')} status={status} onOpen={onOpen} dot={dot} />;
}

/**
 * The editor, in a sheet: the fields, what fills itself in, the send
 * history, and «Сохранить» pinned at the bottom. Saving asks whether to
 * send it to everyone; cancelling that question just saves.
 */
export function InfoSheet({ view, onClose, onChanged }: {
  view: CoachView; onClose: () => void; onChanged: (v: CoachView) => void;
}) {
  const t = useT();
  const [form, setForm] = useState({
    address: view.address ?? '', mapsUrl: view.mapsUrl ?? '', directions: view.directions ?? '', notes: view.notes ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const changed = form.address !== (view.address ?? '') || form.mapsUrl !== (view.mapsUrl ?? '')
    || form.directions !== (view.directions ?? '') || form.notes !== (view.notes ?? '');

  async function sendAll(v: CoachView) {
    if (!(await confirm(t('co.info.askSend', { n: v.reachable })))) return false;
    const r = await api<{ recipients: number; history: CoachView['history'] }>('/api/coach/info/send', { method: 'POST', body: JSON.stringify({}) });
    onChanged({ ...v, history: r.history });
    haptic.success();
    await alert(t('co.info.sent', { n: r.recipients, clients: t.word('co.offer.clientsDat', r.recipients) }));
    return true;
  }

  async function save() {
    setBusy(true);
    try {
      const v = await api<CoachView>('/api/coach/info', { method: 'PUT', body: JSON.stringify(form) });
      onChanged(v);
      if (form.mapsUrl.trim() && !v.mapsUrl) await alert(t('co.info.badUrl'));
      if (v.filled && v.reachable > 0) {
        if (!(await sendAll(v))) { haptic.success(); await alert(t('co.info.saved')); }
      } else {
        haptic.success();
        await alert(v.filled ? t('co.info.saved') : t('co.info.savedEmpty'));
      }
      onClose();
    } catch (err: any) {
      haptic.error();
      await alert(t('co.failed', { message: err.message }));
    } finally { setBusy(false); }
  }

  const field = (key: keyof typeof form, label: Parameters<typeof t>[0], ph: Parameters<typeof t>[0], rows = 0) => (
    <label className="mf" style={{ marginTop: 12 }}>
      <span className="mf__label">{t(label)}</span>
      {rows
        ? <GrowArea className="notearea" rows={rows} value={form[key]} placeholder={t(ph)}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
        : <input value={form[key]} placeholder={t(ph)} inputMode={key === 'mapsUrl' ? 'url' : undefined}
            onChange={(e) => setForm({ ...form, [key]: e.target.value })} />}
    </label>
  );

  return (
    <PinnedSheet title={t('co.set.titleInfo')} onClose={onClose}
      footer={<>
        <button className="paybar__opt" onClick={onClose}>{t('co.offer.cancel')}</button>
        <button className="savebtn" disabled={busy || !changed} onClick={save}>{t('co.rate.save')}</button>
      </>}>
      <div className="card__when">{t('co.info.hint')}</div>
      {!view.filled && <div className="warnline">{t('co.info.empty')}</div>}
      {field('address', 'co.info.address', 'co.info.addressPh', 2)}
      {field('mapsUrl', 'co.info.maps', 'co.info.mapsPh')}
      {field('directions', 'co.info.directions', 'co.info.directionsPh', 3)}
      {field('notes', 'co.info.notes', 'co.info.notesPh', 3)}

      <div className="infoset__auto">
        <div className="infoset__autoh"><span>{t('co.info.auto')}</span><span>{t('co.info.autoFrom')}</span></div>
        {/* Two blocks, like the client sees them: the 1-hour price, then cancelling. */}
        {view.prices.length > 0 && (
          <div className="infoset__autob">
            <div className="iblock__head"><span aria-hidden="true">💶</span>{t('info.prices')}</div>
            <div className="info__price"><span>{t('info.hour')}</span><b>{fmtMoney(Math.round(view.prices[0].price))}</b></div>
          </div>
        )}
        <div className="infoset__autob">
          <div className="iblock__head"><span aria-hidden="true">↩️</span>{t('info.cancel')}</div>
          <p>{t('info.cancelFree', { h: view.cancelHours, hours: t.word('info.hoursW', view.cancelHours) })}</p>
          <p>{t('info.cancelLate')}</p>
        </div>
      </div>

      {view.filled && (
        <>
          <button className="sess__link infoset__send" onClick={() => { haptic.tap(); setPreview(!preview); }}>
            {preview ? t('co.info.hidePreview') : t('co.info.preview')}
          </button>
          {preview && <div style={{ marginTop: 10 }}><InfoCard info={view} /></div>}
          <button className="sess__link infoset__send" disabled={busy || view.reachable === 0}
            onClick={async () => { setBusy(true); try { await sendAll(view); } catch (err: any) { haptic.error(); await alert(t('co.failed', { message: err.message })); } finally { setBusy(false); } }}>
            {t('co.info.sendAll')}
          </button>
          <div className="card__when">
            {t('co.info.reach', { n: view.reachable, clients: t.word('co.info.clientsW', view.reachable) })}
            {view.notInBot > 0 ? t('co.info.notInBot', { n: view.notInBot }) : ''}
          </div>
        </>
      )}

      {view.history.length > 0 && (
        <div className="infoset__hist">
          <div className="mf__label">{t('co.info.history')}</div>
          {view.history.map((h) => (
            <div key={h.id} className="card__when">
              {fmtShortDay(new Date(h.created_at))}, {fmtTime(h.created_at)}
              {' · '}{t('co.info.historyInfo')}{' · '}{h.recipients} {t.word('co.info.clientsW', h.recipients)}
            </div>
          ))}
        </div>
      )}
    </PinnedSheet>
  );
}
